import { Box, Divider, Paper, Typography, useMediaQuery, useTheme } from '@mui/material'
import Stack from '@mui/material/Stack'
import { Fragment, useEffect, useState } from 'react'

import { useBookingContext } from '@providers/booking/booking_context'
import { getAbsolutePetIndex } from '@providers/booking/helper'
import type { TBookingPet } from '@providers/booking/types'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { getServiceTypeFromCtx } from '@shared/offers'
import { ContactToBookContent } from '@views/contact_to_book/contact_to_book_content'
import { GroomingIcon } from 'components-ui/src/asset/grooming_icon'
import { isHiddenForSearchGroups } from 'components-ui/src/offer_card/helper'
import { Spacer3 } from 'components-ui/src/spacer/spacer'
import { GroomingSelector } from './grooming_selector'

export const Grooms = ({
  currentTabIndex,
  showTitle = true,
  showEmptyState = false,
}: {
  currentTabIndex: number
  showTitle?: boolean
  /** When true, render the Contact Us To Book panel if the current tab has no
   * visible grooms. The standalone grooming page sets this (grooming is the whole
   * booking); in-stay grooming (pet add-ons) leaves it false and renders nothing,
   * as it does today. */
  showEmptyState?: boolean
}) => {
  const bookingCtx = useBookingContext()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const currentSpecieId = bookingCtx.state.cart.groups[currentTabIndex]?.specieId ?? ''
  const groomServiceType = getServiceTypeFromCtx(bookingCtx, 'grooming')
  const groomLocationName = groomServiceType?.displayName || ''

  const [petsInGroup, setPetsInGroup] = useState<TBookingPet[]>([])

  useEffect(() => {
    const petsFromGroups: TBookingPet[] = []
    bookingCtx.state.cart.groups[currentTabIndex].pets?.map((pet): void => {
      if (!petsFromGroups.some((p) => p.id === pet.id)) petsFromGroups.push({ ...pet })
      setPetsInGroup(petsFromGroups)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTabIndex])

  // Grooming searches use one search group per pet (flattened across rooms), so
  // map this room's pets to their absolute indexes in the grooming response.
  const roomPetSearchIndexes = (bookingCtx.state.cart.groups[currentTabIndex]?.pets ?? []).map((_pet, petIdx) =>
    getAbsolutePetIndex(bookingCtx.state.cart.groups, currentTabIndex, petIdx)
  )

  const searchResult = (bookingCtx.state.data.grooms ?? []).filter(
    // Matching species and not hidden by Hatch for every pet in this room. A groom
    // hidden for only some pets stays visible (per-pet handling is unchanged).
    (result) =>
      result.offer?.locationSpecies?.id === currentSpecieId && !isHiddenForSearchGroups(result, roomPetSearchIndexes)
  )

  if (showEmptyState && searchResult.length === 0) {
    return <ContactToBookContent />
  }

  return (
    <>
      {searchResult && searchResult.length !== 0 && (
        <>
          <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
              {showTitle && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GroomingIcon sx={{ pt: 0.5, width: 24, height: 24 }} />
                    <Typography variant="h5">{groomLocationName}</Typography>
                  </Stack>
                  <>
                    <Spacer3 />
                  </>
                </>
              )}

              {searchResult.map((o) => {
                return (
                  <Fragment key={o.offer!.id!}>
                    <GroomingSelector groom={o} groupIdx={currentTabIndex} petsInGroup={petsInGroup} />
                    <Divider orientation="horizontal" sx={{ my: 2, marginX: 'auto' }} />
                  </Fragment>
                )
              })}
              <Spacer3 />
            </Paper>
          </Box>
          <Spacer3 />
        </>
      )}
    </>
  )
}
